// ─── How it works — Luồng Play: Off (limit) vs Order (market) ───
// Giải thích chi tiết hai nút vào lệnh trong Chin Bot Monitor:
//   - ORDER (orderDirection) → vào lệnh market ngay, startOrderLoop
//   - OFF   (direction + conditionPrice) → vào lệnh điều kiện, startOffEngine

import { SectionTitle, Card, Pill } from './shared';

export function ChinPlayFlowSection() {
  return (
    <section className="space-y-5">
      <SectionTitle
        id="chin-play"
        icon="ri-play-list-line"
        iconBg="bg-secondary-500/10"
        iconColor="text-secondary-400"
        kicker="Chin Bot — Vào lệnh"
        title="Luồng Play: Off (limit) vs Order (market)"
        subtitle="Mỗi symbol trong bảng Chin Bot có cột Play gồm hai nút vào lệnh độc lập: Order và Off. Cả hai đều dùng chung tham số Price, Max, Vol nhưng cơ chế khởi động và kích hoạt khác hẳn nhau."
      />

      {/* ─── Hai nút tổng quan ─── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-l-4 border-l-emerald-500/50">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
              <i className="ri-flashlight-line text-emerald-400 text-base w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <p className="text-foreground-950 text-sm font-semibold">Order — vào lệnh ngay</p>
              <p className="text-foreground-400 text-[10px] uppercase tracking-wide font-semibold">Market order</p>
            </div>
          </div>
          <p className="text-foreground-600 text-xs leading-relaxed">
            Ngay khi bạn chọn <strong className="text-foreground-900">Buy/Sell</strong> trên nút Order, engine gửi lệnh
            <strong className="text-foreground-900"> market</strong> tới cTrader tại mức giá hiện tại, không cần chờ giá chạm mốc nào.
            Phù hợp khi bạn muốn vào lệnh tức thì theo tín hiệu.
          </p>
        </Card>

        <Card className="border-l-4 border-l-accent-500/50">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-9 h-9 rounded-lg bg-accent-500/10 flex items-center justify-center shrink-0">
              <i className="ri-crosshair-line text-accent-400 text-base w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <p className="text-foreground-950 text-sm font-semibold">Off — vào lệnh điều kiện</p>
              <p className="text-foreground-400 text-[10px] uppercase tracking-wide font-semibold">Limit / conditional order</p>
            </div>
          </div>
          <p className="text-foreground-600 text-xs leading-relaxed">
            Bạn nhập mức giá kích hoạt vào ô <strong className="text-foreground-900">Price</strong>, chọn Buy/Sell trên nút Off.
            Engine sẽ <strong className="text-foreground-900">theo dõi giá theo từng tick</strong> và chỉ vào lệnh khi giá chạm tới mức
            điều kiện đó. Phù hợp khi bạn muốn đặt lệnh chờ ở một vùng giá cụ thể.
          </p>
        </Card>
      </div>

      {/* ─── Luồng Order Now ─── */}
      <Card>
        <p className="text-sm font-heading font-semibold text-foreground-900 mb-4 flex items-center gap-2">
          <i className="ri-flashlight-line text-emerald-400 w-5 h-5 flex items-center justify-center"></i>
          Luồng chi tiết — Order Now (market)
        </p>

        <div className="space-y-3">
          {[
            { n: '1', t: 'Bật nút', d: 'Bấm ORDER → chọn Buy hoặc Sell. Trạng thái orderDirection được lưu và engine order loop khởi động ngay lập tức.' },
            { n: '2', t: 'Đếm vị thế', d: 'Engine đọc số lệnh đang mở hiện tại trên cTrader cho symbol này (getPositionCount).' },
            { n: '3', t: 'Kiểm tra trần lệnh', d: 'Nếu số lệnh đã đạt maxOrders thì dừng (max_reached). Chưa đạt thì tiếp tục.' },
            { n: '4', t: 'Kiểm tra rủi ro', d: 'Chạy 4 rule (spread ≤ 3 pips, single symbol...). Nếu bị chặn thì đợi 3 giây rồi thử lại.' },
            { n: '5', t: 'Đặt lệnh market', d: 'Gửi lệnh BUY/SELL với khối lượng (Vol × 100.000 = đơn vị cTrader) tới gateway → cTrader.' },
            { n: '6', t: 'Xác nhận + lặp', d: 'Chờ cTrader xác nhận vị thế tăng lên. Sau đó lặp lại cho tới khi đủ maxOrders hoặc bạn tắt nút.' },
          ].map((s) => (
            <div key={s.n} className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-md bg-emerald-500/10 text-emerald-400 flex items-center justify-center shrink-0 text-xs font-mono font-semibold">
                {s.n}
              </div>
              <div>
                <p className="text-foreground-900 text-xs font-semibold">{s.t}</p>
                <p className="text-foreground-600 text-xs mt-0.5 leading-relaxed">{s.d}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Pill tone="secondary"><i className="ri-loop-left-line text-[10px]"></i>Vòng lặp tuần tự theo maxOrders</Pill>
          <Pill tone="neutral"><i className="ri-shield-check-line text-[10px]"></i>Hard cap: không bao giờ vượt maxOrders</Pill>
          <Pill tone="neutral"><i className="ri-stop-circle-line text-[10px]"></i>Tắt = đặt lại nút về OFF</Pill>
        </div>
      </Card>

      {/* ─── Luồng Off ─── */}
      <Card>
        <p className="text-sm font-heading font-semibold text-foreground-900 mb-4 flex items-center gap-2">
          <i className="ri-crosshair-line text-accent-400 w-5 h-5 flex items-center justify-center"></i>
          Luồng chi tiết — Off (limit / điều kiện)
        </p>

        <div className="space-y-3">
          {[
            { n: '1', t: 'Nhập mức giá', d: 'Điền giá kích hoạt vào ô Price (conditionPrice). Đây là mốc mà bạn muốn vào lệnh.' },
            { n: '2', t: 'Bật nút Off', d: 'Bấm OFF → chọn Buy hoặc Sell. Engine Off khởi động và bắt đầu theo dõi giá theo từng tick (mỗi 200ms).' },
            { n: '3', t: 'Kiểm tra giá ban đầu', d: 'Engine so sánh giá hiện tại với mức điều kiện để quyết định theo dõi hay tự tắt.' },
            { n: '4', t: 'Theo dõi tới khi chạm giá', d: 'BUY: chờ giá giảm về ≤ mức điều kiện. SELL: chờ giá tăng lên ≥ mức điều kiện.' },
            { n: '5', t: 'Giá chạm → kiểm tra rủi ro', d: 'Khi giá chạm mốc, engine kiểm tra rủi ro rồi chuyển sang đặt lệnh market (giống luồng Order Now).' },
            { n: '6', t: 'Hoàn tất', d: 'Đặt đủ maxOrders rồi dừng, hoặc nút tự reset về OFF khi hoàn thành.' },
          ].map((s) => (
            <div key={s.n} className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-md bg-accent-500/10 text-accent-400 flex items-center justify-center shrink-0 text-xs font-mono font-semibold">
                {s.n}
              </div>
              <div>
                <p className="text-foreground-900 text-xs font-semibold">{s.t}</p>
                <p className="text-foreground-600 text-xs mt-0.5 leading-relaxed">{s.d}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ─── Logic Buy/Sell cụ thể ─── */}
        <div className="mt-5 rounded-lg border border-background-200/70 overflow-hidden">
          <div className="bg-background-100 px-4 py-2.5 border-b border-background-200/70">
            <p className="text-xs font-semibold text-foreground-800">Cách giá kích hoạt hoạt động theo hướng</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="p-4 border-b md:border-b-0 md:border-r border-background-200/70">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-5 h-5 rounded-md bg-emerald-500/10 text-emerald-400 flex items-center justify-center text-[10px] font-bold">B</span>
                <p className="text-foreground-900 text-xs font-semibold">Buy (chờ giá giảm)</p>
              </div>
              <ul className="space-y-1.5">
                <li className="text-foreground-600 text-xs leading-relaxed">
                  Giá hiện tại <strong className="text-foreground-800">đã thấp hơn</strong> mức điều kiện → <span className="text-foreground-500 font-semibold">auto OFF</span> (cơ hội đã qua, không vào).
                </li>
                <li className="text-foreground-600 text-xs leading-relaxed">
                  Giá hiện tại <strong className="text-foreground-800">cao hơn</strong> mức điều kiện → theo dõi, khi giá <strong className="text-emerald-400">giảm xuống ≤ mức</strong> thì kích hoạt.
                </li>
              </ul>
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-5 h-5 rounded-md bg-rose-500/10 text-rose-400 flex items-center justify-center text-[10px] font-bold">S</span>
                <p className="text-foreground-900 text-xs font-semibold">Sell (chờ giá tăng)</p>
              </div>
              <ul className="space-y-1.5">
                <li className="text-foreground-600 text-xs leading-relaxed">
                  Giá hiện tại <strong className="text-foreground-800">đã cao hơn</strong> mức điều kiện → <span className="text-foreground-500 font-semibold">auto OFF</span> (cơ hội đã qua, không vào).
                </li>
                <li className="text-foreground-600 text-xs leading-relaxed">
                  Giá hiện tại <strong className="text-foreground-800">thấp hơn</strong> mức điều kiện → theo dõi, khi giá <strong className="text-rose-400">tăng lên ≥ mức</strong> thì kích hoạt.
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Pill tone="secondary"><i className="ri-radar-line text-[10px]"></i>Theo dõi tick mỗi 200ms</Pill>
          <Pill tone="neutral"><i className="ri-close-circle-line text-[10px]"></i>Giá đã qua mốc → auto OFF</Pill>
          <Pill tone="neutral"><i className="ri-flashlight-line text-[10px]"></i>Kích hoạt xong → chuyển sang market order</Pill>
        </div>
      </Card>

      {/* ─── Bảng so sánh ─── */}
      <Card>
        <p className="text-sm font-heading font-semibold text-foreground-900 mb-4 flex items-center gap-2">
          <i className="ri-scales-3-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
          So sánh nhanh
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-background-200/70">
                <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-foreground-500 uppercase tracking-wide">Tiêu chí</th>
                <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-emerald-400 uppercase tracking-wide">Order (market)</th>
                <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-accent-400 uppercase tracking-wide">Off (limit)</th>
              </tr>
            </thead>
            <tbody>
              {[
                { k: 'Thời điểm vào lệnh', a: 'Ngay lập tức', b: 'Chờ giá chạm mức Price' },
                { k: 'Mức giá điều kiện', a: 'Không cần', b: 'Bắt buộc (ô Price)' },
                { k: 'Cơ chế theo dõi', a: 'Không theo dõi giá', b: 'Tick-by-tick (200ms)' },
                { k: 'Nếu giá đã qua mốc', a: 'Không áp dụng', b: 'Tự động OFF (không vào)' },
                { k: 'Đặt lệnh', a: 'Market order', b: 'Market order (sau khi chạm giá)' },
                { k: 'Phù hợp khi', a: 'Vào ngay theo tín hiệu', b: 'Đặt lệnh chờ ở vùng giá' },
              ].map((r) => (
                <tr key={r.k} className="border-b border-background-200/50">
                  <td className="px-3 py-2.5 text-foreground-700 font-medium">{r.k}</td>
                  <td className="px-3 py-2.5 text-foreground-600">{r.a}</td>
                  <td className="px-3 py-2.5 text-foreground-600">{r.b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ─── Tham số & cơ chế chung ─── */}
      <Card>
        <p className="text-xs font-semibold text-foreground-800 mb-3 flex items-center gap-2">
          <i className="ri-settings-3-line text-primary-400 w-4 h-4 flex items-center justify-center"></i>
          Tham số &amp; cơ chế dùng chung cho cả hai luồng
        </p>
        <div className="space-y-1">
          {[
            { icon: 'ri-price-tag-3-line', color: 'text-accent-400', t: 'Price', d: 'Mức giá điều kiện — chỉ Off dùng. Order bỏ qua ô này.' },
            { icon: 'ri-stack-line', color: 'text-primary-400', t: 'Max', d: 'Số lệnh tối đa được mở. Cả hai luồng đều dừng khi chạm trần.' },
            { icon: 'ri-coin-line', color: 'text-secondary-400', t: 'Vol', d: 'Khối lượng mỗi lệnh (đơn vị lot). Nhân 100.000 để ra đơn vị cTrader.' },
            { icon: 'ri-shield-check-line', color: 'text-emerald-400', t: 'Risk control', d: 'Cả hai đều qua 4 rule (spread, max orders, single symbol, after hours) trước khi gửi lệnh.' },
            { icon: 'ri-lock-line', color: 'text-amber-400', t: 'Distributed lock', d: 'Chống đặt trùng khi nhiều thiết bị cùng vào lệnh một lúc.' },
          ].map((i) => (
            <div key={i.t} className="flex items-start gap-3 py-2.5">
              <div className="w-6 h-6 rounded-md bg-background-200/60 flex items-center justify-center shrink-0 mt-0.5">
                <i className={`${i.icon} ${i.color} text-xs w-4 h-4 flex items-center justify-center`}></i>
              </div>
              <div className="min-w-0">
                <p className="text-foreground-900 text-sm font-medium">{i.t}</p>
                <p className="text-foreground-600 text-xs mt-0.5 leading-relaxed">{i.d}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </section>
  );
}